/** Generate a stable slug from a string (handles Arabic and English). */
export function slugify(input: string): string {
	return input
		.trim()
		.toLowerCase()
		.replace(/[؀-ۿ]/g, "") // strip Arabic for URL-safety
		.replace(/[^a-z0-9-]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.substring(0, 50);
}

/** Normalize Iraqi phone numbers to E.164 (+964...). */
export function normalizeIqPhone(raw: string): string {
	const digits = raw.replace(/\D/g, "");
	if (digits.startsWith("00964")) return `+${digits.substring(2)}`;
	if (digits.startsWith("964")) return `+${digits}`;
	if (digits.startsWith("07")) return `+964${digits.substring(1)}`;
	if (digits.startsWith("7") && digits.length === 10) return `+964${digits}`;
	return raw; // unrecognized — return as-is so caller can decide
}

/**
 * wa.me-ready phone: digits only, no '+'. Normalizes Iraqi local forms
 * (07XXXXXXXXX / 00964 / 964) to the international 9647XXXXXXXXX WhatsApp
 * expects. Returns '' if there are no usable digits. Use this for every
 * WhatsApp share link — a raw "07…" number opens WhatsApp with no contact.
 */
export function toWhatsappNumber(raw: string | null | undefined): string {
	if (!raw) return "";
	return normalizeIqPhone(raw).replace(/\D/g, "");
}

/**
 * Search variants for a phone term typed by staff. Iraqi numbers live in the
 * DB in two formats ("+9647XXXXXXXXX" canonical, legacy "07XXXXXXXXX") while
 * staff type either — so searching "077…" against "+9647…" rows (or the
 * reverse) must still match. Returns the DIGIT sequences a stored phone could
 * contain in either format; callers OR-match each variant with `ILIKE '%v%'`
 * (the '+' never needs to be in the pattern — `%964…%` matches "+964…").
 *
 * Partial numbers work: "0770123" → ["0770123", "9647701234…"-prefix variant
 * "964770123"], both substring-match the full stored number.
 *
 * Returns [] when the term isn't phone-like (letters etc.) so name searches
 * never get noisy phone clauses.
 */
export function iqPhoneSearchVariants(raw: string): string[] {
	const term = raw.trim();
	if (!term) return [];
	// Only digits + common phone punctuation count as a phone-like term.
	if (/[^\d+\-()\s.]/.test(term)) return [];
	const digits = term.replace(/\D/g, "");
	if (digits.length < 2) return [];

	const variants = new Set<string>([digits]);
	// Strip any international prefix down to the national rest, then emit
	// both stored spellings of it.
	let rest: string | null = null;
	if (digits.startsWith("00964")) rest = digits.slice(5);
	else if (digits.startsWith("964")) rest = digits.slice(3);
	else if (digits.startsWith("0") && !digits.startsWith("00"))
		rest = digits.slice(1);
	if (rest && rest.length >= 1) {
		variants.add(`964${rest}`);
		variants.add(`0${rest}`);
	}
	return [...variants];
}

/**
 * Full storage forms a phone may have been saved under, for EXACT-match
 * lookups (find-or-create by phone). New writes are normalized to
 * "+9647XXXXXXXXX", but legacy/imported rows can hold the local
 * "07XXXXXXXXX" spelling — match both so we never create a duplicate
 * patient for the same person.
 */
export function iqPhoneStoredForms(raw: string): string[] {
	const norm = normalizeIqPhone(raw);
	const forms = new Set<string>([norm]);
	if (norm.startsWith("+964")) forms.add(`0${norm.slice(4)}`);
	return [...forms];
}

/** Validate Iraqi mobile phone (basic check). */
export function isValidIqPhone(value: string): boolean {
	const normalized = normalizeIqPhone(value);
	return /^\+9647[3-9]\d{8}$/.test(normalized);
}

/** Generate MRN (Medical Record Number) — clinic-scoped. */
export function generateMrn(prefix: string, sequence: number): string {
	return `${prefix.toUpperCase()}-${String(sequence).padStart(6, "0")}`;
}

/** Sleep helper (for retries). */
export function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Safe JSON parse. */
export function safeJsonParse<T = unknown>(str: string): T | null {
	try {
		return JSON.parse(str) as T;
	} catch {
		return null;
	}
}

/**
 * Clinical safety: find patient allergies that potentially match a
 * medication name. Used by the prescription form to surface a warning
 * before the doctor issues a drug the patient may react to.
 *
 * Algorithm (intentionally conservative — false positives are safer
 * than false negatives in clinical software):
 *  - Lowercase and Arabic-diacritic-strip both sides
 *  - Split each allergy on common separators (/, ، or "and") so a
 *    record like "Penicillin / بنسلين / Amoxicillin" produces 3 tokens
 *  - Each token (≥3 chars) becomes a substring check against the drug
 *  - Drug name is also checked as a substring against the full allergy
 *    text (covers brand-name allergies like "Augmentin" matching itself)
 *
 * Caveat: this is NOT a pharmacology engine. It only catches what the
 * doctor wrote into the allergies list. We rely on the doctor entering
 * broad terms ("Penicillin including derivatives") or specific ones.
 * A future drug-interaction database could replace this with a proper
 * ingredient-level check.
 */
export function findAllergyMatches(
	allergies: ReadonlyArray<{ substance: string }>,
	medicationName: string,
): { substance: string; matchedToken: string }[] {
	const drug = normalizeForAllergyMatch(medicationName);
	if (!drug) return [];

	const matches: { substance: string; matchedToken: string }[] = [];
	for (const allergy of allergies) {
		const raw = allergy.substance;
		if (!raw) continue;
		const fullNorm = normalizeForAllergyMatch(raw);

		// Tokenize on common separators. "Penicillin / Augmentin / بنسلين"
		// → ["penicillin", "augmentin", "بنسلين"].
		const tokens = fullNorm
			.split(/[/,،;]| and | or | و /g)
			.map((t) => t.trim())
			.filter((t) => t.length >= 3);

		let matched: string | null = null;
		for (const token of tokens) {
			if (drug.includes(token) || token.includes(drug)) {
				matched = token;
				break;
			}
		}
		// Fall back to the full allergy string in case the tokenizer split
		// something we shouldn't have ("Vitamin C" → ["vitamin", "c"] —
		// we'd miss it without this fallback).
		if (!matched && fullNorm.length >= 3 && drug.includes(fullNorm)) {
			matched = fullNorm;
		}
		if (matched) {
			matches.push({ substance: raw, matchedToken: matched });
		}
	}
	return matches;
}

function normalizeForAllergyMatch(s: string): string {
	return (
		s
			.trim()
			.toLowerCase()
			// Strip Arabic harakat (diacritics) so بَنِسِلين matches بنسلين.
			.replace(/[ً-ْٰ]/g, "")
			// Collapse whitespace.
			.replace(/\s+/g, " ")
	);
}
